import { Dispatch, ReactElement, SetStateAction, useState } from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Button,
  Card,
  Chip,
  ChipOwnProps,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material'
import {
  CloseIcon,
  CredentialIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  PreviewIcon,
} from 'src/v3/core/icons'
import { Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ContentType,
  downloadFile,
  extensions,
} from 'src/v3/utilities/filesystem'
import { VerificationStatus } from 'src/v3/core/types'
import { VerificationStatusInfo } from '../../verification-status'

type LicenseStatus = 'Active' | 'Expiring Soon' | 'Expired'

const licenseStatusColor: Record<LicenseStatus, ChipOwnProps['color']> = {
  Active: 'success',
  'Expiring Soon': 'warning',
  Expired: 'error',
}

function getLicenseStatus(expiration: string): LicenseStatus {
  let date = dayjs(expiration)
  let daysToExpiration = date.diff(dayjs(), 'day')
  return daysToExpiration < 0
    ? 'Expired'
    : daysToExpiration > 30
      ? 'Active'
      : 'Expiring Soon'
}

type Props = {
  name: string
  number?: string
  expiration?: string
  verifications?: Record<string, VerificationStatus>
  issueDate?: string
  type?: string
  onDownload?: string
  onEdit?: () => void
  onDelete?: () => void
  onError?: Dispatch<SetStateAction<string | undefined>>
  status: Status
}

export let LicenseOverviewCard = ({
  name,
  number,
  expiration,
  verifications,
  issueDate,
  type,
  onDownload,
  onEdit,
  onDelete,
  onError,
  status,
}: Props): ReactElement => {
  if (status === 'pending')
    return (
      <Skeleton variant="rounded" sx={{ minHeight: 62, height: 'inherit' }} />
    )

  let [previewing, setPreviewing] = useState<boolean>(false)
  let [downloading, setDownloading] = useState<boolean>(false)
  let [viewBlob, setViewBlob] = useState<Blob | null>(null)

  let date = dayjs(expiration)
  let licenseStatus = getLicenseStatus(expiration || '')
  let color = licenseStatusColor[licenseStatus]

  return (
    <Card
      sx={{
        p: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        rowGap: 1,
        columnGap: 3,
        bgcolor: 'background.paperDark',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Chip
        color={
          name === 'NREMT' || name === 'Primary License'
            ? 'primary'
            : 'secondary'
        }
        sx={{
          borderRadius: 1,
        }}
        icon={name.includes('License') ? <CredentialIcon /> : undefined}
        label={name}
      />
      {number ? (
        <>
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">#{number}</Typography>
            {type && <Typography variant="body2">({type})</Typography>}
            {verifications && (
              <VerificationStatusInfo
                name={name}
                verifications={verifications}
              />
            )}
          </Box>
          {expiration && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip variant="outlined" label={licenseStatus} color={color} />
              <Box>
                {issueDate && (
                  <Typography variant="subtitle2">
                    Issued {dayjs(issueDate).format('MM/DD/YYYY')}
                  </Typography>
                )}
                <Typography variant="subtitle2">
                  Expire{licenseStatus === 'Expired' ? 'd' : 's'}{' '}
                  {date.format('MM/DD/YYYY')}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}
          >
            {onDownload && (
              <>
                <IconButton
                  size="small"
                  onClick={() => {
                    setPreviewing(true)
                    axios
                      .get<string>(onDownload)
                      .then(pluckData)
                      .then((response) =>
                        axios
                          .get<Blob>(response, { responseType: 'blob' })
                          .catch(() => {
                            // eslint-disable-next-line fp/no-throw, no-throw-literal
                            throw 'File Not Found'
                          }),
                      )
                      .then(pluckData)
                      .then((data) => setViewBlob(data))
                      .catch((err) =>
                        onError?.(
                          err?.response?.data ||
                            err?.message ||
                            'Error Getting Image',
                        ),
                      )
                      .finally(() => setPreviewing(false))
                  }}
                  loading={previewing}
                >
                  <PreviewIcon />
                </IconButton>
                <Dialog
                  open={!!viewBlob}
                  onClose={() => setViewBlob(null)}
                  maxWidth="lg"
                >
                  <Card
                    variant="outlined"
                    sx={{ p: 2, position: 'relative', overflow: 'scroll' }}
                  >
                    <IconButton
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                      size="small"
                      aria-label="Close image preview"
                      onClick={() => setViewBlob(null)}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Typography
                      sx={{
                        textAlign: 'center',
                        fontSize: 20,
                        mb: 0.5,
                      }}
                    >
                      View Image
                    </Typography>
                    <Box>
                      {viewBlob && (
                        <img
                          src={URL.createObjectURL(viewBlob)}
                          alt="View Image"
                        />
                      )}
                    </Box>
                  </Card>
                </Dialog>
                <IconButton
                  size="small"
                  onClick={() => {
                    setDownloading(true)
                    axios
                      .get<string>(onDownload)
                      .then(pluckData)
                      .then((response) =>
                        axios
                          .get<Blob>(response, { responseType: 'blob' })
                          .catch(() => {
                            // eslint-disable-next-line fp/no-throw, no-throw-literal
                            throw 'File Not Found'
                          }),
                      )
                      .then(pluckData)
                      .then((response) =>
                        downloadFile(
                          response,
                          `${name.toLowerCase().split(' ').join('-')}.${extensions[response.type as ContentType]}`,
                        ),
                      )
                      .catch((err) => {
                        onError?.(
                          `Image Download Failed: ${err.response?.data || err}`,
                        )
                      })
                      .finally(() => setDownloading(false))
                  }}
                  loading={downloading}
                >
                  <DownloadIcon />
                </IconButton>
              </>
            )}
            <IconButton size="small" onClick={onEdit}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={onDelete} disabled={!onDelete}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </>
      ) : (
        <>
          <Typography sx={{ flexGrow: 1 }}>
            There is no {name} associated with your account.
          </Typography>
          <Button variant="outlined" onClick={onEdit} disabled={!onEdit}>
            Add {name}
          </Button>
        </>
      )}
    </Card>
  )
}
